import { useTranslation } from 'react-i18next'
import { AnalyticsFilters, PageTitle } from '../components'
import { Outlet, useLocation } from '@tanstack/react-router'
import { useAnalyticsHeaderSlot } from '@/features/Analytics/components/AnalyticsHeaderSlot/useAnalyticsHeaderSlot'
import { AnalyticsHeaderSlotProvider } from '@/features/Analytics/components/AnalyticsHeaderSlot/AnalyticsHeaderSlotProvider'
import { ReportHeader } from '@/features/Reporting/components/ReportHeader/ReportHeader'
import useAnalyticsStore from '@/features/Analytics/stores/useAnalyticsStore/useAnalyticsStore'
import { useMemo } from 'react'
import { RoleRestrictedContent } from '@/components/molecules/RoleRestrictedContent/RoleRestrictedContent'
import { UserType } from '@/hooks'
import { AnalyticsTabs } from '@/features/Analytics/components/AnalyticsTabs/AnalyticsTabs'
import { FilterError } from '@/components/common/FilterError/FilterError'
import { useNodesCommoditiesFiltersError } from '@/hooks/useNodesCommoditiesFiltersError/useNodesCommoditiesFiltersError'

const Analytics = () => {
    const { t } = useTranslation('translations')
    const { slot } = useAnalyticsHeaderSlot()
    const { isFiltersError } = useNodesCommoditiesFiltersError()

    const title = t('sidebar_menu.analytics')
    const reportSettings = useAnalyticsStore(
        (state) => state.benchmarking.reportSettings
    )

    const { pathname } = useLocation()
    const isBenchmarking = pathname.includes('/benchmarking')
    const selectedFilter = useMemo(() => {
        if (!isBenchmarking) return undefined
        return reportSettings.display === 'group'
            ? 'tag'
            : reportSettings.display
    }, [isBenchmarking, reportSettings.display])

    return (
        <div className="grid h-full grow grid-cols-[100%] grid-rows-[auto_1fr]">
            <div className="space-y-6">
                <div>
                    <ReportHeader
                        title={title}
                        renderTitle={() => (
                            <PageTitle
                                label={title}
                                labelTestId="page-title-analytics"
                            />
                        )}
                    >
                        {slot}
                    </ReportHeader>

                    <RoleRestrictedContent
                        allowedRoles={[UserType.CUSTOMER]}
                        strict={false}
                    >
                        <AnalyticsTabs />

                        {isFiltersError ? (
                            <FilterError />
                        ) : (
                            <div
                                className="relative z-30 border-b border-neutral-200 p-6"
                                data-walkthrough="analytics-filters"
                            >
                                <AnalyticsFilters
                                    selectedFilter={selectedFilter}
                                />
                            </div>
                        )}
                    </RoleRestrictedContent>
                </div>
            </div>

            {!isFiltersError && <Outlet />}
        </div>
    )
}

const AnalyticsWithContext = () => (
    <AnalyticsHeaderSlotProvider>
        <Analytics />
    </AnalyticsHeaderSlotProvider>
)

export default AnalyticsWithContext
